/**
 * A Yjs provider that syncs over the MQTT connection this app already holds.
 *
 * WHY A CUSTOM ONE. The published Yjs providers assume a websocket server you run (y-websocket) or
 * a WebRTC mesh (y-webrtc). This suite has neither and does not want either: it already maintains an
 * authenticated MQTT connection to AWS IoT Core per signed-in browser, with the per-connection
 * policy built by our own authorizer. Standing up a second real-time transport beside it would mean
 * a second thing to authenticate, scale and keep alive.
 *
 * PEER TO PEER, NOT THROUGH A LAMBDA. Browsers publish document updates to each other on a
 * per-revision topic and IoT Core fans them out. Routing keystrokes through a Lambda would put a
 * cold start in the typing path and bill per character.
 *
 * THE TOPIC IS A TRANSPORT, NOT AN AUTHORIZATION BOUNDARY. Everything here is ephemeral. The
 * authority over what the document IS lives in Save Document Content, which asks t1 whether the
 * revision is still open before it writes. A too-wide topic grant means somebody could watch or
 * disturb a live session; it can never mean the stored document changed.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO:
 *
 *   * It does not carry the initial document. IoT Core caps a payload at 128KB, and a document of
 *     any size exceeds that long before it is interesting. The body is fetched over HTTPS and only
 *     the deltas travel here.
 *   * It does not persist. Durability is the debounced save, not the message bus.
 *   * It does not resolve "who is right" -- that is what the CRDT is for, and the whole reason a
 *     lossy, unordered, at-least-once transport is good enough underneath it. Duplicate and
 *     out-of-order updates are not an error condition here; applying one twice is a no-op.
 */
import * as Y from "yjs";
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate,
         removeAwarenessStates } from "y-protocols/awareness";

/** Matches the IoT policy and the rule name; see cres-aws-t11 handlers/authorizer/rules/iot.py. */
const TOPIC_PREFIX = "cres_web_t11/v1";

/** Sub-topics. Separate so a late joiner can ask for state without being handed presence noise. */
const UPDATE = "update";
const AWARE = "awareness";
const HELLO = "hello";
/** A whole-document answer to a hello. Separate from `update` so it can be REFUSED -- see onMessage. */
const STATE = "state";
/** "Your copy is not the one that is stored." */
const STALE = "stale";

export type YMqttProviderOptions = {
    /** The mqtt.js client from useMqttClient. */
    client: any;
    /** The revision whose body this is. One document, one topic.  */
    documentUSID: string;
    /**
     * The tenant. IT IS IN THE TOPIC, not merely alongside it: the IoT policy grants
     * `doc/<realm>/*` and nothing wider, so a browser cannot subscribe to another tenant's
     * documents even if it asks. That is the one failure here that would be serious, and it is
     * refused by IoT Core rather than by this code.
     */
    realm: string;
    doc: Y.Doc;
    awareness: Awareness;
    /**
     * Told that this editor's copy is behind the stored one.
     *
     * The provider cannot decide what to do about it -- stopping the autosave and asking the person
     * to reload is the editor's business -- but it is the only thing that can NOTICE.
     */
    onStale?: () => void;
};

/**
 * Base64 for a Uint8Array, because MQTT payloads here are JSON and Yjs updates are binary.
 *
 * Chunked rather than String.fromCharCode(...bytes): spreading a large update blows the argument
 * limit and throws RangeError, which would show up only on a big paste.
 */
export function encode(bytes: Uint8Array): string {
    let binary = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as any);
    }
    return btoa(binary);
}

export function decode(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

/**
 * Instrumentation, off unless asked for.
 *
 * A COLLABORATION BUG CANNOT BE REASONED ABOUT FROM ONE SIDE. "Edits flow one way" is a statement
 * about two browsers, and every explanation for it -- a dropped subscribe, a dropped publish, a
 * policy refusal, a listener that was removed -- looks identical from inside either one. So the
 * provider can say exactly what it sent, what it dropped and what arrived, and the two transcripts
 * are read side by side.
 *
 * Switched on per browser with `localStorage.setItem("docs.debug", "1")`, because a console line
 * per keystroke is intolerable the rest of the time.
 */
function debugging(): boolean {
    try {
        return localStorage.getItem("docs.debug") === "1";
    } catch {
        return false;                                       // private window; never throw on a log
    }
}

export class YMqttProvider {
    private readonly client: any;
    private readonly doc: Y.Doc;
    private readonly awareness: Awareness;
    private readonly base: string;
    private readonly origin = {};
    private destroyed = false;
    private readonly debug = debugging();
    /** Counted so a transcript says "the 7th update", not just "an update". */
    private sent = 0;
    private got = 0;

    private log(what: string, detail: object = {}) {
        if (!this.debug) return;
        // eslint-disable-next-line no-console
        console.log(`[ydoc ${this.doc.clientID}] ${what}`,
                    {connected: !!this.client?.connected, sent: this.sent, got: this.got,
                     topic: this.base, ...detail});
    }

    /**
     * Has any peer sent us document state?
     *
     * The editor asks before seeding from the HTTP body. A late joiner receives the whole
     * document over MQTT, and applying the fetched copy ON TOP of it inserts the text a
     * second time -- a CRDT merges two copies rather than recognising them as the same
     * words, so the document silently doubles.
     */
    hasPeerState = false;

    /**
     * Has this provider actually managed to say hello?
     *
     * The editor waits for this before it starts counting down to seeding. Seeding is the
     * irreversible move -- it is what doubles a document -- and the whole justification for it is
     * "we asked and nobody answered". Until the question has left the building, a silence means
     * nothing at all.
     */
    announced = false;

    /**
     * WHICH STORED BODY THIS EDITOR'S STATE WAS BUILT FROM -- the ContentDigest of the last version
     * it loaded or saved. This is the authority the join never had.
     *
     * A Yjs room has no notion of which copy is right: every peer answers a hello with everything
     * it holds and the newcomer merges all of it. A tab left open since before somebody else saved
     * answers with a document that is no longer the stored one, the newcomer merges it, and the
     * next autosave writes it back. That is how a procedure loses its text while two windows both
     * look connected and healthy.
     *
     * The digest settles it without a server in the room. A browser that has just fetched the
     * document knows what IS stored; a peer that says it was built from a different stored version
     * is, by definition, not holding the current document.
     */
    private baseDigest: string | null = null;

    /** Did a peer answer our hello with a copy we refused? The editor says so on screen. */
    refusedPeerState = false;

    private readonly onStale?: () => void;

    /** Called after every load and every successful save. */
    setBaseDigest(digest: string | null) {
        this.baseDigest = digest ?? null;
    }

    constructor({ client, documentUSID, realm, doc, awareness, onStale }: YMqttProviderOptions) {
        this.client = client;
        this.doc = doc;
        this.awareness = awareness;
        // KEYED ON THE PARKOUR DOC, NOT THE REVISION, and the difference is no longer
        // academic: a revision holds several of these now, and a topic per revision would put
        // two people editing the procedure and its appendix into ONE collaborative room --
        // their Y.Docs would merge and each would watch the other's text appear inside their
        // own. One room per document is what "editing this document together" means.
        this.base = `${TOPIC_PREFIX}/doc/${realm}/${documentUSID}`;

        this.client.on("message", this.onMessage);
        this.onStale = onStale;
        this.doc.on("update", this.onLocalUpdate);
        this.awareness.on("update", this.onLocalAwareness);
        window.addEventListener("beforeunload", this.onUnload);
        this.log("constructed");

        // JOINED ON CONNECT, NOT IN THE CONSTRUCTOR, AND THIS WAS A REAL BUG -- found by reading
        // two instrumented transcripts side by side, where the second window says plainly:
        //
        //     constructed            {connected: false}
        //     DROPPED (not connected) ... hello
        //
        // The provider is built as soon as the editor mounts; the shared MQTT client is often still
        // opening its socket. publish() drops when not connected -- it has to, there is nowhere to
        // put the bytes -- so the hello was lost and never retried, and NOBODY EVER TOLD THIS
        // WINDOW WHAT THE DOCUMENT SAID.
        //
        // It receives later edits, so two windows opened at leisure look perfectly synchronised,
        // which is why this survived three rounds of staring at it. The damage is at the join:
        // with no reply, hasPeerState stays false, the grace period expires, and the editor seeds
        // from the SAVED body -- on top of a live document that may have moved on. A CRDT merges
        // two copies rather than recognising them as the same words, so the text doubles. Peak's
        // own sample document is currently carrying three copies of its Purpose and Scope.
        //
        // `connect` also fires on every RECONNECT, which is what we want: AWS IoT closes the socket
        // when the token's lifetime elapses, and a window coming back needs to re-ask rather than
        // assume it missed nothing. Subscribing again is idempotent; so is a second hello.
        if (this.client?.connected) this.join();
        this.client.on("connect", this.join);
    }

    /**
     * Subscribe, then announce.
     *
     * IN THAT ORDER, because the replies to our own hello arrive on the topics we are subscribing
     * to -- announcing first is a race we would lose on a fast broker.
     */
    private join = () => {
        if (this.destroyed) return;
        this.client.subscribe([`${this.base}/${UPDATE}`,
                               `${this.base}/${STATE}`,
                               `${this.base}/${STALE}`,
                               `${this.base}/${AWARE}`,
                               `${this.base}/${HELLO}`], { qos: 0 },
                              (error: any, granted: any) => {
                                  this.log("subscribed", {error: error?.message ?? null, granted});
                                  // ANNOUNCE ONLY ONCE THE BROKER HAS CONFIRMED. A hello answered
                                  // before our SUBSCRIBE lands is a reply nobody hears.
                                  // THE HELLO CARRIES WHAT WE BELIEVE IS STORED. A peer whose own
                                  // base differs learns from this that it is the stale one -- it
                                  // has to, because it is the one that will otherwise keep saving.
                                  this.publish(HELLO, { kind: "hello", base: this.baseDigest });
                                  this.announced = true;
                              });
    };

    /**
     * Every message says who sent it, and we ignore our own.
     *
     * AN MQTT BROKER DELIVERS YOUR OWN PUBLISH BACK TO YOU when you are subscribed to the topic,
     * and this client subscribes to every topic it publishes on -- it has to, that is how it hears
     * the room. So a hello comes straight back, gets answered with our own whole state, and that
     * answer comes back too.
     *
     * Harmless while nothing read it: applying your own update is a no-op in Yjs. It stopped being
     * harmless the moment `hasPeerState` was actually wired up -- a browser alone in the room
     * answered its own hello, concluded somebody else was holding the document, and never seeded
     * from the stored body. One character on screen where a procedure should be.
     */
    private publish(topic: string, payload: object) {
        const body = JSON.stringify({ ...payload, from: this.doc.clientID });
        // DROPPED, AND SAID SO. This is the one place a message can vanish without any error
        // anywhere, and "we were not connected yet" is indistinguishable from "it was sent and
        // lost" unless the provider says which.
        if (this.destroyed || !this.client?.connected) {
            this.log("DROPPED (not connected)", {sub: topic, bytes: body.length,
                                                 destroyed: this.destroyed});
            return;
        }
        this.sent += 1;
        this.log("published", {sub: topic, bytes: body.length});
        this.client.publish(`${this.base}/${topic}`, body, { qos: 0 });
    }

    private onLocalUpdate = (update: Uint8Array, origin: unknown) => {
        // NOT THE ONES WE JUST APPLIED. An update arriving from the wire is applied with this
        // provider as its origin; re-publishing it would bounce the same bytes around every peer
        // forever. Yjs would survive it -- applying an update twice is a no-op -- but the traffic
        // would grow with the square of the number of editors.
        if (origin === this.origin) return;
        this.publish(UPDATE, { kind: "update", update: encode(update) });
    };

    private onLocalAwareness = ({ added, updated, removed }: any) => {
        const changed = added.concat(updated, removed);
        this.publish(AWARE, {
            kind: "awareness",
            update: encode(encodeAwarenessUpdate(this.awareness, changed)),
        });
    };

    private onMessage = (topic: string, payload: Uint8Array) => {
        if (this.destroyed) return;
        if (!topic.startsWith(`${this.base}/`)) {
            // Logged too: a message on the RIGHT connection and the WRONG topic is what a stale
            // provider, or a room keyed on the wrong identifier, looks like from here.
            if (topic.includes("/doc/")) this.log("ignored (other topic)", {arrived: topic});
            return;
        }
        let message: any;
        try {
            message = JSON.parse(new TextDecoder().decode(payload));
        } catch {
            return;                                  // not ours, or truncated; never throw on the bus
        }

        // OUR OWN VOICE, COMING BACK OFF THE BROKER. Not counted and not acted on -- counting it
        // would make the transcript say a room of one had traffic in it.
        if (message.from === this.doc.clientID) return;

        this.got += 1;
        this.log("received", {sub: topic.slice(this.base.length + 1), kind: message.kind});

        if (message.kind === "update") {
            this.hasPeerState = true;
            Y.applyUpdate(this.doc, decode(message.update), this.origin);
            return;
        }
        if (message.kind === "awareness") {
            applyAwarenessUpdate(this.awareness, decode(message.update), this.origin);
            return;
        }
        if (message.kind === "state") {
            // THE ONE MESSAGE THAT CAN BE REFUSED, and the reason this protocol has it at all. An
            // `update` is a delta somebody just typed; a `state` is a whole document offered by a
            // peer, and accepting one from a peer that was built on a different stored version is
            // exactly how a procedure gets replaced by an old copy of itself. A CRDT merge cannot
            // be undone, so the check has to happen BEFORE applyUpdate, not after.
            //
            // Only when BOTH sides know their base. A document with nothing stored yet has no
            // digest on either side, and refusing everything then would break the ordinary case of
            // two people starting one together.
            if (this.baseDigest && message.base && message.base !== this.baseDigest) {
                this.refusedPeerState = true;
                this.log("refused peer state", {theirs: String(message.base).slice(0, 12),
                                                ours: this.baseDigest.slice(0, 12)});
                this.publish(STALE, { kind: "stale", base: this.baseDigest });
                return;
            }
            this.hasPeerState = true;
            Y.applyUpdate(this.doc, decode(message.update), this.origin);
            return;
        }

        if (message.kind === "stale") {
            // SOMEBODY WHO HAS READ THE STORE SAYS WE ARE BEHIND. Believe them: they fetched, we
            // have been sitting here.
            if (this.baseDigest && message.base && message.base !== this.baseDigest) {
                this.log("told we are stale", {theirs: String(message.base).slice(0, 12),
                                               ours: this.baseDigest.slice(0, 12)});
                this.onStale?.();
            }
            return;
        }

        if (message.kind === "hello") {
            // A NEWCOMER HAS JUST READ THE STORE, so its base is fresher than ours by construction.
            // If it does not match, we are the stale one -- and we are the one that will otherwise
            // keep autosaving an old document over a newer one.
            if (this.baseDigest && message.base && message.base !== this.baseDigest) {
                this.log("a newcomer disagrees with our base", {theirs: String(message.base).slice(0, 12),
                                                               ours: this.baseDigest.slice(0, 12)});
                this.onStale?.();
                return;                              // and do NOT answer with our copy
            }

            // Reply with everything we have, SAYING WHAT IT WAS BUILT FROM. The newcomer merges it;
            // so does everyone else, for whom it is a no-op.
            this.publish(STATE, { kind: "state", base: this.baseDigest,
                                  update: encode(Y.encodeStateAsUpdate(this.doc)) });
            this.publish(AWARE, {
                kind: "awareness",
                update: encode(encodeAwarenessUpdate(this.awareness,
                                                     Array.from(this.awareness.getStates().keys()))),
            });
        }
    };

    /**
     * Tell the room we are gone.
     *
     * Without this a closed tab leaves its cursor behind, and a document accumulates ghost editors
     * who appear to be present and are not. Awareness has its own timeout, but it is measured in
     * tens of seconds and the ghost is visible for all of them.
     */
    private onUnload = () => {
        removeAwarenessStates(this.awareness, [this.doc.clientID], "unload");
    };

    destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        this.onUnload();
        window.removeEventListener("beforeunload", this.onUnload);
        this.doc.off("update", this.onLocalUpdate);
        this.awareness.off("update", this.onLocalAwareness);
        this.client?.off?.("message", this.onMessage);
        this.client?.off?.("connect", this.join);

        // NOT UNSUBSCRIBED, DELIBERATELY, AND THIS WAS A BUG. The mqtt client is shared by the
        // whole app, so these topics are not ours to withdraw: if this provider is being replaced
        // -- a dependency changed, the component remounted -- its successor has already subscribed
        // to the same topics on the same connection, and SUBSCRIBE and UNSUBSCRIBE are separate
        // round trips that can land at the broker in either order. Losing that race leaves the
        // client subscribed to nothing while believing it is subscribed.
        //
        // The symptom was perfectly asymmetric and took a while to credit: the window that lost
        // the race published fine and received nothing, so edits flowed one way between two windows
        // that both looked connected. Removing the message listener above is enough -- an
        // unobserved subscription costs a few bytes and breaks nothing.
    }
}
